import { useState, useEffect, useCallback, useRef } from 'react';
import ApiService from '../services/ApiService';

// Re-export useAuth from the new AuthContext
export { useAuth } from '../contexts/AuthContext';

// Custom hook for API calls with loading and error states
export const useApiCall = (apiFunction, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const isExecutingRef = useRef(false);

  const execute = useCallback(async (...args) => {
    // Prevent duplicate calls while one is in progress
    if (isExecutingRef.current) {
      console.log('[useApiCall] Request already in progress, skipping duplicate');
      return;
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    try {
      isExecutingRef.current = true;
      setLoading(true);
      setError(null);
      
      abortControllerRef.current = new AbortController();
      const result = await apiFunction(...args);
      
      setData(result);
      return result;
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        console.log('[useApiCall] Request aborted');
        return;
      }
      
      const errorInfo = ApiService.handleApiError(err);
      setError(errorInfo.message);
      throw err;
    } finally {
      isExecutingRef.current = false;
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [apiFunction]);

  useEffect(() => {
    // Only execute if we have valid dependencies
    if (dependencies.length > 0 && dependencies.every(dep => dep != null)) {
      execute();
    } else {
      setLoading(false);
    }

    // Cleanup function to abort on unmount or dependency change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      isExecutingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies); // Use dependencies directly, not spread

  return { data, loading, error, execute, refetch: execute };
};

// Helper function to get user ID from different user object structures
const getUserId = (user) => {
  if (!user) return null;
  return user.id || user.documentId || user._id || null;
};

// Custom hook for gift cards and user data
export const useGiftCardsData = (user) => {
  const userId = getUserId(user);
  const userIdRef = useRef(userId);
  
  
  // Only update refs if values actually changed
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);
  
  const apiCall = useApiCall(
    () => {
      const currentUserId = userIdRef.current;
      
      if (!currentUserId) {
        throw new Error('User not authenticated');
      }
      console.log('[useGiftCardsData] Fetching for user:', currentUserId);
      return ApiService.getGiftCardsAndUser(currentUserId);
    },
    [userId] // Only re-fetch when these primitives change
  );
  
  // Override the loading state if we don't have required data
  if (!userId) {
    return {
      ...apiCall,
      loading: false,
      error: 'User not authenticated'
    };
  }
  
  return apiCall;
};

// Custom hook for points data
export const usePointsData = (user, token) => {
  const userId = getUserId(user);
  const userIdRef = useRef(userId);
  const tokenRef = useRef(token);
  
  // Only update refs if values actually changed
  useEffect(() => {
    userIdRef.current = userId;
    tokenRef.current = token;
  }, [userId, token]);
  
  const apiCall = useApiCall(
    () => {
      const currentUserId = userIdRef.current;
      
      if (!currentUserId) {
        throw new Error('User not authenticated');
      }
      console.log('[usePointsData] Fetching points data for user:', currentUserId);
      return ApiService.getPointsData(currentUserId);
    },
    [userId, token] // Only re-fetch when these primitives change
  );
  
  // Override the loading state if we don't have required data
  if (!userId) {
    return {
      ...apiCall,
      loading: false,
      error: 'User not authenticated'
    };
  }
  
  return apiCall;
};

// Custom hook for reward codes (admin)
export const useRewardCodes = (token) => {
  return useApiCall(
    () => ApiService.getRewardCodes(),
    []
  );
};

// Custom hook for scanning barcodes
export const useBarcodeScanner = () => {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const scanBarcode = async (barcode, user, token) => {
    const userId = getUserId(user);
    if (!userId) {
      throw new Error('User ID is required for scanning');
    }
    
    try {
      setError(null);
      const response = await ApiService.scanBarcode(barcode, userId);
      setResult(response);
      return response;
    } catch (err) {
      const errorInfo = ApiService.handleApiError(err);
      setError(errorInfo.message);
      throw err;
    }
  };

  const clearResults = () => {
    setResult(null);
    setError(null);
  };

  return {
    scanning,
    setScanning,
    result,
    error,
    scanBarcode,
    clearResults
  };
};

// Custom hook for redemptions
export const useRedemption = () => {
  const [redeeming, setRedeeming] = useState(false);

  const redeemGiftCard = async (productRef, quantity) => {
    try {
      setRedeeming(true);
      const response = await ApiService.createRedemption({
        productRef,
        quantity: quantity || 1
      });
      
      // Response already formatted by ApiService
      return response;
    } catch (error) {
      console.error('useRedemption error:', error);
      throw error;
    } finally {
      setRedeeming(false);
    }
  };

  return {
    redeeming,
    redeemGiftCard
  };
};

const hooks = {
  useApiCall,
  useGiftCardsData,
  usePointsData,
  useRewardCodes,
  useBarcodeScanner,
  useRedemption
};

export default hooks;